import {InvalidTokenError} from '@packages/utils';
import {parseCookies} from 'nookies';
import {useCallback, useState} from 'react';
import {
  type ColumnRepositoryProps,
  deleteColumnMyClips,
  updateColumnMyClips,
} from '../../../core/repositories/clipsRepository';
import {useIsSessionExpired} from '../../../core/usecases/auth/useIsSessionExpired';

type HandleClipUpdateProps = Omit<ColumnRepositoryProps, 'cookies'> & {
  clipCount: number;
  clippedAt: string;
};

export function useHandleColumnClip({
  columnId,
  clipCount,
  clippedAt,
}: HandleClipUpdateProps) {
  const [clipCountUpdated, setClipCountUpdated] = useState<number>(clipCount);
  const [isClipped, setIsClipped] = useState<boolean>(Boolean(clippedAt));
  const {updateIsSessionExpired} = useIsSessionExpired();

  const deleteColumnUseCase = useCallback(
    async function deleteUseCase() {
      const cookies = parseCookies();
      const result = await deleteColumnMyClips({
        cookies,
        columnId,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      if (!result.ok) {
        return;
      }

      setIsClipped(false);
      setClipCountUpdated(clipCount - 1);
    },
    [columnId, clipCount],
  );

  const updateColumnUseCase = useCallback(
    async function updateUseCase() {
      const cookies = parseCookies();
      const result = await updateColumnMyClips({
        cookies,
        columnId,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      if (!result.ok) {
        return;
      }

      setIsClipped(true);
      setClipCountUpdated(clipCount - 1 + 1);
    },
    [columnId, clipCount],
  );

  const handleClip = useCallback(async () => {
    // MEMO: ここでクリップの状態を判定して、クリップ済みの場合はクリップを解除する
    // 各ユースケースを呼び出す
    if (isClipped) {
      await deleteColumnUseCase();
      return;
    }

    await updateColumnUseCase();
  }, [deleteColumnUseCase, isClipped, updateColumnUseCase]);

  return {handleClip, clipCountUpdated, isClipped};
}
